import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/cn';
import type { ProductVariant } from '@/types/product';

interface ProductVariantSelectorProps {
  variants: ProductVariant[];
  selectedVariantId: number | null;
  onSelect: (variantId: number) => void;
  className?: string;
}

export function ProductVariantSelector({
  variants,
  selectedVariantId,
  onSelect,
  className,
}: ProductVariantSelectorProps) {
  const selected = variants.find((v) => v.id === selectedVariantId) ?? null;

  // Build distinct color list — first variant per color is canonical
  const colorMap = useMemo(() => {
    const map = new Map<string, ProductVariant>();
    for (const v of variants) {
      if (!map.has(v.color)) map.set(v.color, v);
    }
    return map;
  }, [variants]);

  // Sizes available for selected color
  const availableSizes = useMemo(() => {
    if (!selected) return [];
    return variants.filter((v) => v.color === selected.color);
  }, [variants, selected]);

  const handleColorPick = (color: string) => {
    const candidate = variants.find(
      (v) => v.color === color && (!selected || v.size === selected.size)
    ) ?? variants.find((v) => v.color === color);
    if (candidate) onSelect(candidate.id);
  };

  const handleSizePick = (variantId: number) => {
    onSelect(variantId);
  };

  if (variants.length === 0) return null;

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      {/* Colors */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-label-mono text-[color:var(--color-text-muted)]">
            Color
          </span>
          {selected && (
            <span className="text-[13px] text-[color:var(--color-text-secondary)]">
              {selected.color}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          {Array.from(colorMap.values()).map((v) => {
            const active = v.color === selected?.color;
            return (
              <motion.button
                key={v.color}
                type="button"
                onClick={() => handleColorPick(v.color)}
                aria-label={`Color ${v.color}`}
                aria-pressed={active}
                title={v.color}
                whileTap={{ scale: 0.92 }}
                className={cn(
                  // Image swatches read better a touch larger; keep round.
                  'relative h-9 w-9 overflow-hidden rounded-full border transition-shadow',
                  active
                    ? 'ring-2 ring-[color:var(--color-brand-magenta)] ring-offset-2 ring-offset-[color:var(--color-bg-mid)] border-white/30'
                    : 'border-white/15 hover:border-white/40'
                )}
                style={v.imageUrl ? undefined : { backgroundColor: v.colorHex ?? '#888' }}
              >
                {v.imageUrl && (
                  <img
                    src={v.imageUrl}
                    alt={v.color}
                    loading="lazy"
                    className="h-full w-full object-cover"
                    draggable={false}
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Sizes */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-label-mono text-[color:var(--color-text-muted)]">
            Size
          </span>
          {selected && (
            <span className="text-[13px] text-[color:var(--color-text-secondary)]">
              {selected.size}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {availableSizes.map((v) => {
            const oos = v.status !== 'ACTIVE' || v.stock <= 0;
            const active = v.id === selected?.id;
            return (
              <motion.button
                key={v.id}
                type="button"
                disabled={oos}
                onClick={() => handleSizePick(v.id)}
                aria-pressed={active}
                whileTap={!oos ? { scale: 0.95 } : undefined}
                className={cn(
                  'min-w-[52px] rounded-md border px-3 py-2 font-[family-name:var(--font-label)] text-[13px] uppercase tracking-[0.08em] transition-colors',
                  active
                    ? 'border-[color:var(--color-brand-magenta)] bg-[color:var(--color-brand-magenta)]/10 text-white'
                    : 'border-white/15 text-[color:var(--color-text-secondary)] hover:border-white/40 hover:text-white',
                  oos && 'opacity-40 cursor-not-allowed line-through'
                )}
              >
                {v.size}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
